import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { adminLogin, forgotPassword } from '../lib/api';

const LOGO_URL = "/pt-logo.png?v=2";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await adminLogin(email, password);
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('admin_email', response.data.email);
      toast.success('Login successful');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success('If this email is registered, a password reset link has been sent.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="transport-forgot-password-page">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-12 w-auto" />
              <span className="text-2xl font-bold text-white font-['Outfit']">Platinum Touch</span>
            </Link>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">Reset Password</h1>
            <p className="text-slate-400 mt-2">Enter your email to receive a reset link</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <Label htmlFor="forgot-email" className="text-slate-700 font-medium">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="pl-11 h-12"
                    required
                    data-testid="transport-forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={forgotLoading}
                className="w-full bg-[#38B6FF] hover:bg-blue-500 text-white h-12 font-semibold"
                data-testid="transport-forgot-submit-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="text-sm text-slate-500 hover:text-[#38B6FF] transition-colors inline-flex items-center gap-2"
                data-testid="transport-back-to-login"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6" data-testid="logo-link-admin">
            <img src={LOGO_URL} alt="Platinum Touch" className="h-12 w-auto" />
            <span className="text-2xl font-bold text-white font-['Outfit']">Platinum Touch</span>
          </Link>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Admin Portal</h1>
          <p className="text-slate-400 mt-2">Sign in to manage bookings</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="admin-login-form">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="pl-11 h-12"
                  required
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-11 h-12"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#38B6FF] hover:bg-blue-500 text-white h-12 font-semibold"
              data-testid="admin-login-btn"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-[#38B6FF] hover:text-blue-600 transition-colors font-medium"
              data-testid="transport-forgot-password-btn"
            >
              Forgot Password?
            </button>
          </div>

          <div className="mt-4 text-center">
            <Link 
              to="/" 
              className="text-sm text-slate-500 hover:text-[#38B6FF] transition-colors"
              data-testid="back-to-site-link"
            >
              ← Back to website
            </Link>
          </div>
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          Contact admin for login credentials
        </p>
      </div>
    </div>
  );
}
